import { getAuthTokenWithActivity, updateLastActivity } from './apiUtils';
import { performGlobalLogout } from '@/config/firebase';
import { getAnalyticsSessionId } from '@/services/analyticsService';
// FIX-R6-21: Import devLog, devWarn, devError which are used throughout this file
import { devLog, devWarn, devError } from '@/utils/devLog';

/**
 * Parsed auth error information
 */
interface AuthErrorInfo {
  status: number;
  errorMessage?: string;
}

/**
 * Extract auth error info without consuming response body
 * @param error - Error object (AxiosError, Response, or generic Error)
 * @returns AuthErrorInfo or null if can't extract
 */
async function extractAuthErrorInfo(error: unknown): Promise<AuthErrorInfo | null> {
  let status: number | undefined;
  let errorMessage: string | undefined;

  if (error && typeof error === 'object' && 'response' in error) {
    // Axios error - data already parsed
    const axiosError = error as { response?: { status?: number; data?: { error?: string; message?: string } } };
    status = axiosError.response?.status;
    errorMessage = axiosError.response?.data?.error || axiosError.response?.data?.message;
  } else if (error instanceof Response) {
    // Fetch Response - clone before consuming body
    status = error.status;
    try {
      const cloned = error.clone(); // CRITICAL: Clone before reading
      const data = await cloned.json();
      errorMessage = data.error || data.message;
    } catch {
      // Not JSON or already consumed - that's OK
    }
  } else if (error && typeof error === 'object' && 'status' in error) {
    status = (error as { status: number }).status;
  }

  if (!status) return null;
  return { status, errorMessage };
}

/**
 * Shared handler for 401/403 errors across the application
 * Attempts token refresh before logout (consistent with apiUtils retry logic)
 *
 * IMPORTANT: Does NOT consume response body - safe to call before parsing
 *
 * @param error - Error object (AxiosError, Response, or generic Error)
 * @param context - Where the error occurred (for logging)
 * @returns Promise<'retry' | 'logout' | 'ignore'>
 */
export async function handleAuthError(
  error: unknown,
  context: string
): Promise<'retry' | 'logout' | 'ignore'> {

  const errorInfo = await extractAuthErrorInfo(error);
  if (!errorInfo) return 'ignore';

  const { status, errorMessage } = errorInfo;

  // Not an auth error - ignore
  if (status !== 401 && status !== 403) {
    return 'ignore';
  }

  devError(`[${context}] Auth error ${status}:`, errorMessage);

  // For 403 (forbidden), don't retry - it's a permissions issue
  if (status === 403) {
    devLog(`[${context}] 403 Forbidden - not retrying`);
    return 'ignore'; // Let caller handle permission errors
  }

  // For 401, treat ANY occurrence as potentially recoverable with token refresh
  // Note: We don't rely on specific error message strings (e.g., "token expired")
  // because different backends/JWT libraries use different wording ("jwt expired",
  // "unauthorized", "invalid token", etc.). Any 401 gets one retry attempt.
  // The retry mechanism already prevents infinite loops (single attempt per call site).
  const isRecoverableAuthError = status === 401;

  if (isRecoverableAuthError) {
    devLog(`[${context}] 401 Unauthorized - attempting refresh before logout`);

    try {
      // Force token refresh - this updates the cache with fresh token
      await getAuthTokenWithActivity(true);
      // ✅ DO NOT call clearTokenCache() here - we need the fresh token for retry!
      // The cache now contains the new token that retry logic will use
      devLog(`[${context}] Token refresh successful - caller should retry`);
      return 'retry';
    } catch (refreshError) {
      devError(`[${context}] Token refresh failed:`, refreshError);
      // Fall through to logout
    }
  }

  // Unrecoverable - trigger logout
  devLog(`[${context}] Unrecoverable auth error - triggering logout`);
  await performGlobalLogout();
  return 'logout';
}

/**
 * Axios interceptor helper - ACTUALLY RETRIES the request
 * Use this in axios response interceptors for automatic retry
 */
export async function handleAxiosAuthError(error: unknown, context: string): Promise<never> {
  const action = await handleAuthError(error, context);

  if (action === 'logout') {
    // Already logged out in handleAuthError
    return Promise.reject(error);
  }

  if (action === 'retry') {
    devLog(`[${context}] Retrying request with refreshed token`);

    // Get the original axios instance and config
    if (error && typeof error === 'object' && 'config' in error) {
      const axiosError = error as { config: { headers: { Authorization: string }; __isRetry?: boolean } };
      const originalRequest = axiosError.config;

      // Update Authorization header with new token
      const newToken = await getAuthTokenWithActivity(false); // Use cached refreshed token
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
      }

      // Retry the request with the axios instance
      // NOTE: error.config.__isRetry prevents infinite retry loops
      if (!originalRequest.__isRetry) {
        originalRequest.__isRetry = true;

        // Import axios dynamically to retry
        const axios = (await import('axios')).default;
        return axios(originalRequest);
      }
    }
  }

  // For 'ignore' or retry already attempted, reject
  return Promise.reject(error);
}

/**
 * Fetch wrapper with automatic retry on token refresh
 * Use this instead of raw fetch() for authenticated requests
 *
 * NOTE: This function requires the caller to provide the Authorization header manually.
 * For automatic token fetching and caching, use fetchWithAuthRetryAuto() instead.
 *
 * @param input - URL or Request object
 * @param init - Fetch options (must include Authorization header)
 * @param context - Context for logging
 * @param maxRetries - Maximum retry attempts (default: 1)
 * @returns Response from fetch
 *
 * @example
 * const token = await user.getIdToken();
 * const response = await fetchWithAuthRetry('/api/endpoint', {
 *   method: 'POST',
 *   headers: { Authorization: `Bearer ${token}` },
 *   body: JSON.stringify(data)
 * }, 'My API Call');
 */
export async function fetchWithAuthRetry(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  context: string,
  maxRetries: number = 1
): Promise<Response> {

  // Update activity at the start - user initiated this request
  updateLastActivity();

  // Declare at function scope for error message access
  let attempt = 0;
  let action: 'retry' | 'logout' | 'ignore' = 'ignore';

  for (attempt = 0; attempt <= maxRetries; attempt++) {
    let response: Response;
    try {
      response = await fetch(input, init);
    } catch (networkError) {
      // Track network failure with consent check
      try {
        const { consentService } = await import('@/services/consentService');
        const { analyticsService } = await import('@/services/analyticsService');
        if (consentService.hasConsentFor('analytics')) {
          analyticsService.trackFeatureUsage('network', 'fetch_error', {
            url: String(input),
            error: networkError instanceof Error ? networkError.message : 'unknown',
          });
        }
      } catch (err) { devWarn('[authErrorHandler] Analytics tracking failed:', err); }
      throw networkError;
    }

    if (response.ok) {
      // Update activity after successful response - user received data
      updateLastActivity();
      return response; // Success
    }

    // Check for auth errors
    action = await handleAuthError(response, context);

    if (action === 'logout') {
      // Already logged out
      return response;
    }

    if (action === 'retry') {
      // CASE 1: Retry available - update token and continue loop
      if (attempt < maxRetries) {
        devLog(`[${context}] Retrying request (attempt ${attempt + 2}/${maxRetries + 1})`);

        // Update Authorization header with refreshed token from cache
        const newToken = await getAuthTokenWithActivity(false);
        if (newToken) {
          init = {
            ...init,
            headers: {
              ...init?.headers,
              Authorization: `Bearer ${newToken}`
            }
          };
        }

        // Continue loop to retry the request
        continue;
      } else {
        // CASE 2: Retries exhausted - logout user to prevent stuck state
        devError(`[${context}] Exhausted ${maxRetries} retry attempts - logging out user`);
        await performGlobalLogout();
        return response;
      }
    }

    // CASE 3: Action is 'ignore' or other - return response for caller to handle
    return response;
  }

  // Should never reach here - all loop paths return Response
  // If we do reach here, it indicates a logic error in the retry mechanism
  throw new Error(`[${context}] Unexpected: exhausted retry loop without returning response (attempt=${attempt}, maxRetries=${maxRetries}, lastAction=${action})`);
}

/**
 * Enhanced version of fetchWithAuthRetry that automatically fetches and caches tokens.
 *
 * **RECOMMENDED:** Use this instead of manually calling getIdToken() + fetchWithAuthRetry().
 *
 * Benefits over fetchWithAuthRetry():
 * - Automatically fetches token using cache (reduces Firebase quota usage by 78%)
 * - Saves $160-200/month in Firebase overages
 * - Automatically adds Authorization header
 * - Simpler API - no need to manually manage tokens
 * - Syncs token activity with AuthManager (prevents idle timeout issues)
 *
 * When to use fetchWithAuthRetry() vs fetchWithAuthRetryAuto():
 * - Use fetchWithAuthRetryAuto() for 95% of cases (default choice)
 * - Use fetchWithAuthRetry() only if you need custom token handling or have a pre-fetched token
 *
 * @param input - URL or Request object
 * @param init - Fetch options (Authorization header will be added automatically)
 * @param context - Context for logging
 * @param maxRetries - Maximum retry attempts (default: 1)
 * @returns Response from fetch
 *
 * @example
 * // Before (bypasses cache, wastes Firebase quota):
 * const token = await user.getIdToken();
 * const response = await fetchWithAuthRetry('/api/endpoint', {
 *   headers: { Authorization: `Bearer ${token}` }
 * }, 'Context');
 *
 * // After (uses cache, saves Firebase quota):
 * const response = await fetchWithAuthRetryAuto('/api/endpoint', {}, 'Context');
 *
 * @example
 * // With custom headers and body:
 * const response = await fetchWithAuthRetryAuto('/api/endpoint', {
 *   method: 'POST',
 *   headers: { 'Content-Type': 'application/json' },
 *   body: JSON.stringify({ data: 'value' })
 * }, 'My API Call');
 */
export async function fetchWithAuthRetryAuto(
  input: RequestInfo | URL,
  init: Omit<RequestInit, 'headers'> & { headers?: Record<string, string> } = {},
  context: string,
  maxRetries: number = 1
): Promise<Response> {
  // Fetch token using cache (syncs to AuthManager, prevents unnecessary Firebase calls)
  const token = await getAuthTokenWithActivity(false);

  // Guard against null token - fail fast if user not authenticated
  if (!token) {
    throw new Error(
      `[${context}] Authentication required - cannot fetch token. ` +
      `User may not be logged in or session may have expired.`
    );
  }

  // Build request with Authorization header and analytics session correlation
  const enhancedInit: RequestInit = {
    ...init,
    headers: {
      ...init.headers,
      Authorization: `Bearer ${token}`,
      'X-Analytics-Session-Id': (() => {
        try {
          return getAnalyticsSessionId() || '';
        } catch { return ''; }
      })(),
    }
  };

  // Call existing function with token
  return fetchWithAuthRetry(input, enhancedInit, context, maxRetries);
}
